import { z } from "zod";
import { actorIdentity, actorIdentitySchema, requireCapability, requireTenant, } from "./authority.js";
import { canonicalSha256 } from "./canonicalize.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, assuranceClassSchema, evidenceBindingSchema, protocolIdSchema, protocolTimestampSchema, protocolVersionIdSchema, protocolVersionSchema, sha256DigestSchema, verificationStatusSchema, } from "./identifiers.js";
import { assertCurrentVerdictReceipt, machineReadableVerdictReceiptSchema, } from "./verdict.js";
import { resolveStoredVerdict } from "../verdict-law.js";
export const enforcementDispositionSchema = z.enum([
    "ALLOW",
    "HOLD",
    "BLOCK",
    "RETURN",
    "RETRY",
    "NEEDS_HUMAN",
    "ALLOW_BY_OVERRIDE",
]);
export const railActionKindSchema = z.enum([
    "none",
    "merge",
    "deploy",
    "write",
    "pay",
    "refund",
    "retry",
    "mark_done",
]);
export const enforcementPolicySchema = z
    .object({
    ref: protocolIdSchema,
    version: protocolVersionIdSchema,
    digest: sha256DigestSchema,
    pass: z.enum(["ALLOW", "HOLD"]),
    fail: z.enum(["BLOCK", "RETURN"]),
    unknown: z.enum(["HOLD", "NEEDS_HUMAN"]),
    notRun: z.enum(["RETURN", "RETRY", "NEEDS_HUMAN"]),
    /**
     * What to do with a verdict that is not yet due.
     *
     * OPTIONAL, not required and not `.default()`. Required would break every
     * policy literal already in the tree and every client that POSTs a policy
     * body through the served API. `.default()` would make this schema's INPUT
     * type differ from its OUTPUT type, which the `Operation<…, z.infer<Body>,
     * …>` contracts forbid — measured, not guessed: it red two of them. So the
     * defaulting decision lives at the single read site below, named
     * {@link DEFAULT_PENDING_DISPOSITION}, where it is greppable.
     *
     * RETRY is that default, because re-queueing is the entire behavioural
     * difference between PENDING and UNKNOWN. A deployment that would rather
     * stall than retry says `HOLD`.
     *
     * NOTE FOR THE COVERAGE REG DELIBERATELY PRESERVED: `policy.notRun`'s
     * RETRY branch became unreachable for any receipt minted after the fold,
     * because every contract carries at least one obligation and a non-empty
     * aggregate never returns NOT_RUN. REG kept that branch under test on a
     * pre-fold receipt rather than lose it. This slot makes RETRY reachable
     * again on the LIVE path — a PENDING receipt minted today routes here — so
     * the disposition is exercised by current receipts, not only historical
     * ones. `policy.notRun` is untouched and REG's pre-fold test still holds.
     */
    pending: z.enum(["RETRY", "HOLD"]).optional(),
    requireProspectiveForAction: z.boolean(),
    railActionOnAllow: railActionKindSchema,
    railActionOnReturn: z.enum(["none", "refund"]).optional(),
    railActionOnRetry: z.enum(["none", "retry"]).optional(),
})
    .strict();
export const enforcementDecisionSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    receiptId: protocolIdSchema,
    receiptPayloadDigest: sha256DigestSchema,
    binding: evidenceBindingSchema,
    originalVerdict: verificationStatusSchema,
    assurance: assuranceClassSchema,
    policy: enforcementPolicySchema,
    disposition: enforcementDispositionSchema,
    decidedBy: actorIdentitySchema,
    decidedAt: protocolTimestampSchema,
    reasonCodes: z.array(protocolIdSchema),
    override: z
        .object({
        actor: actorIdentitySchema,
        reason: z.string().trim().min(1),
        expiresAt: protocolTimestampSchema,
        scope: z.string().trim().min(1),
    })
        .strict()
        .nullable(),
})
    .strict();
export const railActionIntentSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    decisionId: protocolIdSchema,
    receiptId: protocolIdSchema,
    receiptPayloadDigest: sha256DigestSchema,
    kind: railActionKindSchema,
    target: z.string().trim().min(1),
    idempotencyKey: sha256DigestSchema,
    requestedAt: protocolTimestampSchema,
    takesCustody: z.literal(false),
})
    .strict();
export const enforcementEffectSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    decisionId: protocolIdSchema,
    intentId: protocolIdSchema,
    idempotencyKey: sha256DigestSchema,
    attemptNumber: z.number().int().positive(),
    requestDigest: sha256DigestSchema,
    state: z.enum(["PENDING", "APPLIED", "FAILED", "SUPERSEDED"]),
    connectorRef: protocolIdSchema,
    externalRef: z.string().trim().min(1).nullable(),
    attemptedAt: protocolTimestampSchema,
    completedAt: protocolTimestampSchema.nullable(),
    responseDigest: sha256DigestSchema.nullable(),
    reasonCodes: z.array(protocolIdSchema),
})
    .strict();
/**
 * The disposition a policy that does not name one gets for a PENDING verdict.
 *
 * RETRY, and it is the reachability REG preserved coverage for: `policy.notRun`'s
 * RETRY branch became unreachable for any receipt minted after the fold (every
 * contract carries at least one obligation, and a non-empty aggregate never
 * returns NOT_RUN). This constant puts RETRY back on the LIVE path.
 */
export const DEFAULT_PENDING_DISPOSITION = "RETRY";
function dispositionFor(status, policy) {
    if (status === "PASS")
        return policy.pass;
    // Named before the tail, which returns the NOT_RUN disposition — the most
    // permissive of the four. MISMATCH takes FAIL's disposition: a demonstrated
    // violation is never enforced more softly than a refusal to confirm.
    if (status === "FAIL" || status === "MISMATCH")
        return policy.fail;
    if (status === "UNKNOWN")
        return policy.unknown;
    // Before the tail, which is NOT_RUN's disposition. A pending verdict routed
    // into `notRun` would be reported as "nothing was looked at" when something
    // was looked at and is merely not yet due.
    if (status === "PENDING")
        return policy.pending ?? DEFAULT_PENDING_DISPOSITION;
    return policy.notRun;
}
export function decideEnforcement(input) {
    const receipt = assertCurrentVerdictReceipt(machineReadableVerdictReceiptSchema.parse(input.receipt));
    const policy = enforcementPolicySchema.parse(input.policy);
    requireTenant(input.actor, receipt.payload.binding.tenantId);
    requireCapability(input.actor, "enforcement:decide");
    // READ TIME, not stored time. A receipt signed PENDING keeps saying PENDING
    // forever — the payload is digest-bound and the store has no UPDATE — so the
    // disposition must be decided against what that verdict means AT
    // `decidedAt`, not against the bytes. `decidedAt` is the injected clock; this
    // function never reads one of its own.
    const read = resolveStoredVerdict({ verdict: receipt.payload, now: input.decidedAt });
    let disposition = dispositionFor(read.status, policy);
    // The EFFECTIVE verdict leads, because it is the one the disposition came
    // from. When nothing resolved it is byte-identical to the stored verdict, so
    // every non-PENDING receipt produces exactly the reason codes it did before.
    const reasonCodes = [`verdict-${read.status.toLowerCase()}`];
    if (read.resolved) {
        // The stored verdict and WHY it no longer reads that way, disclosed rather
        // than silently replaced: a decision record that showed only the resolved
        // status would make a receipt look like it had said something it never did.
        reasonCodes.push(`pending-resolved-${read.reason}`, `pending-stored-${receipt.payload.aggregateStatus.toLowerCase()}`);
    }
    if (disposition === "ALLOW" &&
        policy.requireProspectiveForAction &&
        receipt.payload.assurance !== "PROSPECTIVE") {
        disposition = "HOLD";
        reasonCodes.push("prospective-assurance-required");
    }
    let override = null;
    if (input.override) {
        requireTenant(input.override.actor, receipt.payload.binding.tenantId);
        requireCapability(input.override.actor, "review:override");
        disposition = "ALLOW_BY_OVERRIDE";
        reasonCodes.push("named-override");
        override = {
            actor: actorIdentity(input.override.actor),
            reason: input.override.reason,
            expiresAt: input.override.expiresAt,
            scope: input.override.scope,
        };
    }
    return enforcementDecisionSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: receipt.payload.binding.tenantId,
        receiptId: receipt.payload.id,
        receiptPayloadDigest: receipt.payloadDigest,
        binding: receipt.payload.binding,
        originalVerdict: receipt.payload.aggregateStatus,
        assurance: receipt.payload.assurance,
        policy,
        disposition,
        decidedBy: actorIdentity(input.actor),
        decidedAt: input.decidedAt,
        reasonCodes,
        override,
    });
}
export function createRailActionIntent(input) {
    const decision = enforcementDecisionSchema.parse(input.decision);
    const allowed = decision.disposition === "ALLOW" || decision.disposition === "ALLOW_BY_OVERRIDE";
    const kind = allowed
        ? decision.policy.railActionOnAllow
        : decision.disposition === "RETURN"
            ? (decision.policy.railActionOnReturn ?? "none")
            : decision.disposition === "RETRY"
                ? (decision.policy.railActionOnRetry ?? "none")
                : "none";
    const idempotencyKey = canonicalSha256({
        receiptPayloadDigest: decision.receiptPayloadDigest,
        receiptId: decision.receiptId,
        kind,
        target: input.target,
    }, input.sha256);
    return railActionIntentSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: decision.tenantId,
        decisionId: decision.id,
        receiptId: decision.receiptId,
        receiptPayloadDigest: decision.receiptPayloadDigest,
        kind,
        target: input.target,
        idempotencyKey,
        requestedAt: input.requestedAt,
        takesCustody: false,
    });
}
export function createEnforcementEffect(input) {
    const intent = railActionIntentSchema.parse(input.intent);
    return enforcementEffectSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: intent.tenantId,
        decisionId: intent.decisionId,
        intentId: intent.id,
        idempotencyKey: intent.idempotencyKey,
        attemptNumber: input.attemptNumber ?? 1,
        requestDigest: canonicalSha256(intent, input.sha256),
        state: input.state,
        connectorRef: input.connectorRef,
        externalRef: input.externalRef ?? null,
        attemptedAt: input.attemptedAt,
        completedAt: input.completedAt ?? null,
        responseDigest: input.responseDigest ?? null,
        reasonCodes: input.reasonCodes ?? [],
    });
}
